/**
 * `<MobileMapView/>` — the editorial Map, phone layout (#1043, Wave 6 / M1).
 *
 * On a phone the fixed-coordinate tree canvas (`<TaskCanvas/>`) pans badly, so
 * the mobile Map is **outline-first**: the editorial `<OutlineEditor/>` is the
 * primary surface (tap a row → its detail bottom-sheet), with a compact tab
 * strip up top (My work / Map / Members — the wireframe `V_M1`) and a bottom
 * action bar (⊞ canvas · ⤿ rewind · + task). The canvas is still reachable
 * (the "⊞ canvas" toggle drops back to the desktop tree surface), but the
 * outline is the default — it scrolls cleanly in a narrow viewport.
 *
 * This is the LEAD's mobile Map. It renders ONLY when `useIsMobile()` is true
 * AND the editorial theme is on; the desktop / legacy path is the existing
 * `<LeadMapEditor/>` body, byte-unchanged (the gate lives in the caller). So
 * three states stay safe: desktop (any flag) → unchanged; mobile + flag OFF →
 * unchanged (today's desktop body in a narrow viewport); mobile + flag ON →
 * this layout.
 *
 * It is presentational over the lead editor's shared state + mutations: it
 * receives the already-wired outline props, the rewind/canvas toggles, and the
 * "+ Add" handler — never its own data source (§A7). The drawer / rewind
 * surfaces are owned by the parent, so this view only hosts the outline +
 * chrome.
 *
 * Brand (§A9): factual, calm; no scores; the tab + bar labels mirror the
 * wireframe verbatim. Isolation: taskmap-local + the sanctioned shell utilities
 * only — no scheduler domain import.
 */

import { Link } from "react-router-dom";

import { useMapTheme } from "../theme/MapThemeContext";
import { OutlineEditor, type OutlineEditorProps } from "./OutlineEditor";

export interface MobileMapViewProps {
  /** The team id — for the Members tab link. */
  teamId: string | undefined;
  /** The editorial outline's wired props (shared verbatim with the desktop). */
  outline: OutlineEditorProps;
  /** Drop back to the desktop tree canvas surface (the "⊞ canvas" toggle). */
  onShowCanvas: () => void;
  /** Open the rewind surface — present only when `task_rewind` is on. */
  onOpenRewind?: (() => void) | undefined;
  /** The focal "+ task" action (a sub-task under the focused node, else a root). */
  onAddTask: () => void;
}

/**
 * The phone Map: a tab strip, the outline as the scrolling primary surface, and
 * a thumb-reach bottom action bar. "Map" is the active tab; "My work" /
 * "Members" link to the team's member-facing surfaces (the lead is a member
 * too). The outline + add flow reuse the lead editor's shared state.
 */
export function MobileMapView({
  teamId,
  outline,
  onShowCanvas,
  onOpenRewind,
  onAddTask,
}: MobileMapViewProps) {
  const theme = useMapTheme();
  const c = theme.palette;

  const membersTo = teamId ? `/teams/${teamId}/map/members` : "#";

  return (
    <div
      data-testid="mobile-map-view"
      className="flex h-[calc(100vh-8rem)] flex-col"
      style={{ backgroundColor: c.canvas, fontFamily: theme.fonts.sans }}
    >
      {/* Tab strip (`V_M1`): My work · Map · Members. "Map" is active (this is
          the Map surface); the others are links to the team's member-facing
          surfaces. The lead's own Map is the outline below. */}
      <div
        data-testid="mobile-map-tabs"
        className="flex border-b px-2"
        style={{ borderColor: c.rule, backgroundColor: c.cardBg }}
      >
        <Link
          to={membersTo}
          data-testid="mobile-map-tab-mywork"
          className="px-3.5 py-2.5 text-[12.5px]"
          style={{ color: c.muted, fontFamily: theme.fonts.sans }}
        >
          My work
        </Link>
        <span
          data-testid="mobile-map-tab-map"
          aria-current="page"
          className="px-3.5 py-2.5 text-[12.5px] font-medium"
          style={{ color: c.ink, borderBottom: `1.5px solid ${c.ink}` }}
        >
          Map
        </span>
        <Link
          to={membersTo}
          data-testid="mobile-map-tab-members"
          className="px-3.5 py-2.5 text-[12.5px]"
          style={{ color: c.muted, fontFamily: theme.fonts.sans }}
        >
          Members
        </Link>
      </div>

      {/* The outline — the scrolling primary surface. A mono "OUTLINE (TAP TO
          ZOOM)" kicker frames it per the wireframe; tapping a row opens its
          detail bottom-sheet (the outline's `onOpenNode`, wired by the parent). */}
      <div className="min-h-0 flex-1 overflow-auto" data-testid="mobile-map-outline">
        <div
          className="px-4 pt-3 pb-1 text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
        >
          Outline (tap to zoom)
        </div>
        <OutlineEditor {...outline} compact />
      </div>

      {/* Bottom action bar (`V_M1`): ⊞ canvas · ⤿ rewind · + task. The canvas
          toggle drops back to the tree surface; rewind is present only when its
          gate is on; "+ task" is the focal primary (filled). */}
      <div
        data-testid="mobile-map-actionbar"
        className="flex items-center gap-2 border-t px-3 py-2.5"
        style={{ borderColor: c.rule, backgroundColor: c.bgRecess }}
      >
        <button
          type="button"
          data-testid="mobile-map-canvas"
          onClick={onShowCanvas}
          className="flex flex-1 min-h-tap items-center justify-center rounded-md border text-xs transition-colors"
          style={{ borderColor: c.rule2, color: c.ink, backgroundColor: c.cardBg }}
        >
          ⊞ canvas
        </button>
        {onOpenRewind && (
          <button
            type="button"
            data-testid="mobile-map-rewind"
            onClick={onOpenRewind}
            className="flex flex-1 min-h-tap items-center justify-center rounded-md border text-xs transition-colors"
            style={{ borderColor: c.rule2, color: c.ink, backgroundColor: c.cardBg }}
          >
            ⤿ rewind
          </button>
        )}
        <button
          type="button"
          data-testid="mobile-map-add"
          onClick={onAddTask}
          className="flex flex-1 min-h-tap items-center justify-center rounded-md text-xs font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: c.teal, color: "#FFFFFF" }}
        >
          + task
        </button>
      </div>
    </div>
  );
}
